import { CheckOutlined } from "@ant-design/icons";
import {
  Badge,
  Button,
  Collapse,
  Flex,
  Input,
  Select,
  Skeleton,
  Switch,
  Typography,
} from "antd";
import { useMemo, useState } from "react";
import { connect, type DispatchProp } from "react-redux";
import { actions as routerActions } from "redux-router5";
import { Card } from "../../common/components/Card.tsx";
import { NoDataConnected } from "../../common/components/NoDataComp.tsx";
import { PageTitleContainer } from "../../common/components/PageTitle.tsx";
import { resolveNotificationTarget } from "../../common/notifications/notificationTarget.ts";
import {
  getNotificationTypeConfig,
  resolveIconColors,
} from "../../common/notifications/notificationTypeConfig.tsx";
import { formatRelativeTime } from "../../common/utils/formatRelativeTime.ts";
import { notificationActions } from "../../features/notification/notificationSlice.ts";
import ConnectedAppLayout from "../AppLayout.tsx";
import { routeNames } from "../common/routes.ts";
import type { RootState } from "../store.ts";
import styles from "./NotificationsPage.module.css";
import {
  formatSenderName,
  groupNotifications,
  type NotificationGroup,
} from "./notificationGrouping.ts";

function humanizeType(type: string): string {
  return type
    .toLowerCase()
    .split("_")
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(" ");
}

const VIEW_ROUTE_BY_KIND: Partial<Record<NotificationGroup["kind"], string>> = {
  ticket: routeNames.TICKET_VIEW,
  turbine: routeNames.TURBINE_VIEW,
  location: routeNames.LOCATION_VIEW,
  client: routeNames.CLIENT_VIEW,
  project: routeNames.PROJECT_VIEW,
};

type NotificationsPageProps = ReturnType<typeof withNotifications> & DispatchProp;

export const NotificationsPage = (props: NotificationsPageProps) => {
  const { notifications, technicianIds, loading, intl, dispatch } = props;
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);
  const [unreadOnly, setUnreadOnly] = useState(false);

  const technicianIdSet = useMemo(() => new Set(technicianIds), [technicianIds]);

  const technicianNotifications = useMemo(
    () =>
      notifications.filter(
        (n) => n.sender?.id != null && technicianIdSet.has(n.sender.id)
      ),
    [notifications, technicianIdSet]
  );

  const availableTypes = useMemo(() => {
    const types = new Set<string>();
    for (const n of technicianNotifications) {
      if (n.notification_type) types.add(n.notification_type);
    }
    return Array.from(types).sort();
  }, [technicianNotifications]);

  const groups = useMemo(
    () => groupNotifications(technicianNotifications),
    [technicianNotifications]
  );

  const filteredGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    return groups
      .map((group) => {
        const events = group.events.filter((event) => {
          if (typeFilter && event.notification_type !== typeFilter) return false;
          if (unreadOnly && event.read_at) return false;
          return true;
        });
        return { ...group, events, unreadCount: events.filter((e) => !e.read_at).length };
      })
      .filter((group) => {
        if (group.events.length === 0) return false;
        if (!term) return true;
        if (group.label.toLowerCase().includes(term)) return true;
        return group.events.some((e) =>
          formatSenderName(e.sender).toLowerCase().includes(term)
        );
      });
  }, [groups, search, typeFilter, unreadOnly]);

  const totalUnread = groups.reduce((sum, group) => sum + group.unreadCount, 0);

  const navigateToEntity = (group: NotificationGroup) => {
    const routeName = VIEW_ROUTE_BY_KIND[group.kind];
    if (routeName) {
      dispatch(routerActions.navigateTo(routeName, { id: group.entityId }));
    }
  };

  const navigateToEvent = (event: NotificationGroup["events"][number]) => {
    const target = resolveNotificationTarget(event);
    if (target?.section) {
      dispatch(routerActions.navigateTo(routeNames.TICKET_VIEW, target));
    }
  };

  const markGroupRead = (group: NotificationGroup) => {
    const unreadIds = group.events
      .filter((event) => !event.read_at && event.id)
      .map((event) => event.id as string);
    if (unreadIds.length > 0) {
      dispatch(notificationActions.requestMarkRead(unreadIds));
    }
  };

  return (
    <ConnectedAppLayout>
      <PageTitleContainer
        title={intl.formatMessage({ id: "wo_notifications_title" })}
        subtitle={intl.formatMessage(
          { id: "wo_notifications_subtitle" },
          { no: totalUnread }
        )}
      >
        <Card>
          <Flex vertical gap={16}>
            <Flex gap={8} wrap align="center">
              <Input.Search
                allowClear
                placeholder={intl.formatMessage({
                  id: "wo_notifications_search_placeholder",
                })}
                style={{ maxWidth: 320 }}
                onChange={(event) => setSearch(event.target.value)}
              />
              <Select
                allowClear
                placeholder={intl.formatMessage({
                  id: "wo_notifications_type_filter_placeholder",
                })}
                style={{ minWidth: 220 }}
                value={typeFilter}
                onChange={setTypeFilter}
                options={availableTypes.map((type) => ({
                  value: type,
                  label: humanizeType(type),
                }))}
              />
              <Flex align="center" gap={8}>
                <Switch checked={unreadOnly} onChange={setUnreadOnly} />
                <Typography.Text>
                  {intl.formatMessage({ id: "wo_notifications_unread_only" })}
                </Typography.Text>
              </Flex>
            </Flex>

            {loading && <Skeleton active paragraph={{ rows: 6 }} />}

            {!loading && filteredGroups.length === 0 && <NoDataConnected />}

            {!loading && filteredGroups.length > 0 && (
              <Collapse
                items={filteredGroups.map((group) => ({
                  key: group.key,
                  label: (
                    <Flex
                      align="center"
                      justify="space-between"
                      gap={8}
                      className={styles.groupHeader}
                    >
                      <Flex align="center" gap={8}>
                        <Typography.Text
                          className={
                            group.unreadCount > 0 ? styles.unreadTitle : undefined
                          }
                        >
                          {group.label}
                        </Typography.Text>
                        {group.unreadCount > 0 && (
                          <Badge count={group.unreadCount} size="small" />
                        )}
                      </Flex>
                      <Flex align="center" gap={8}>
                        <Typography.Text type="secondary">
                          {formatRelativeTime(group.latestCreatedAt)}
                        </Typography.Text>
                        {VIEW_ROUTE_BY_KIND[group.kind] && (
                          <Button
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              navigateToEntity(group);
                            }}
                          >
                            {intl.formatMessage({ id: "wo_notifications_open_button" })}
                          </Button>
                        )}
                        {group.unreadCount > 0 && (
                          <Button
                            size="small"
                            icon={<CheckOutlined />}
                            onClick={(event) => {
                              event.stopPropagation();
                              markGroupRead(group);
                            }}
                          >
                            {intl.formatMessage({
                              id: "wo_notifications_mark_read_button",
                            })}
                          </Button>
                        )}
                      </Flex>
                    </Flex>
                  ),
                  children: (
                    <Flex vertical>
                      {group.events.map((event) => {
                        const typeConfig = getNotificationTypeConfig(
                          event.notification_type
                        );
                        const colors = resolveIconColors(typeConfig.color);
                        const isUnread = !event.read_at;
                        const deepLinkable = Boolean(
                          resolveNotificationTarget(event)?.section
                        );
                        return (
                          <Flex
                            key={event.id}
                            align="center"
                            gap={12}
                            className={`${styles.eventRow} ${isUnread ? styles.eventRowUnread : ""}`}
                          >
                            <div
                              className={styles.iconCircle}
                              style={{ background: colors.bg, color: colors.fg }}
                            >
                              {typeConfig.icon}
                            </div>
                            <Flex vertical flex={1}>
                              <Typography.Text strong={isUnread}>
                                {event.message}
                              </Typography.Text>
                              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                {formatSenderName(event.sender)}
                                {" · "}
                                {event.created_at
                                  ? formatRelativeTime(event.created_at)
                                  : ""}
                              </Typography.Text>
                            </Flex>
                            {deepLinkable && (
                              <Button
                                size="small"
                                type="link"
                                onClick={() => navigateToEvent(event)}
                              >
                                {intl.formatMessage({
                                  id: "wo_notifications_open_button",
                                })}
                              </Button>
                            )}
                          </Flex>
                        );
                      })}
                    </Flex>
                  ),
                }))}
              />
            )}
          </Flex>
        </Card>
      </PageTitleContainer>
    </ConnectedAppLayout>
  );
};

const withNotifications = (state: RootState) => ({
  notifications: state.notification.list,
  technicianIds: state.notification.technicianIds,
  loading: state.notification.loading,
  intl: state.intl.datetimeIntl,
});

export const ConnectedNotificationsPage = connect(withNotifications)(NotificationsPage);
export default ConnectedNotificationsPage;
